import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ArrowLeft,
  CheckCircle2,
  CircleAlert,
  CircleHelp,
  Mail,
  Monitor,
  User as UserIcon,
} from "lucide-react";

import { AdminShell } from "@/components/admin/admin-shell";
import { Card } from "@/components/ui/card";
import { LocalDateTime } from "@/components/ui/local-datetime";
import {
  QA_CATEGORIES,
  QA_TEST_CASES,
  type QaCategory,
  findTestCase,
} from "@/lib/qa/test-cases";
import { cn } from "@/lib/utils";

type ResultRow = {
  id: string;
  testCaseId: string;
  testCaseTitle: string;
  result: "PASS" | "FAIL" | "OTHER";
  notes: string | null;
};

type QaSessionDetail = {
  id: string;
  name: string;
  email: string;
  role: string;
  userAgent: string | null;
  notes: string | null;
  startedAt: string;
  submittedAt: string | null;
  results: ResultRow[];
};

export function AdminQaSessionPage() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const [session, setSession] = useState<QaSessionDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!sessionId) return;
    const controller = new AbortController();
    setLoading(true);

    fetch(`/api/admin/qa/sessions/${sessionId}`, {
      credentials: "include",
      signal: controller.signal,
    })
      .then(async (res) => {
        if (res.status === 404) throw new Error("Session not found");
        if (!res.ok) {
          const body = (await res.json().catch(() => ({}))) as { error?: string };
          throw new Error(body.error ?? `HTTP ${res.status}`);
        }
        return res.json() as Promise<QaSessionDetail>;
      })
      .then((d) => {
        setSession(d);
        setLoading(false);
      })
      .catch((err: Error) => {
        if (err.name === "AbortError") return;
        setError(err.message);
        setLoading(false);
      });

    return () => controller.abort();
  }, [sessionId]);

  if (loading) {
    return (
      <AdminShell>
        <main className="brand-bg min-h-[calc(100vh-3.5rem)]">
          <div className="mx-auto flex max-w-5xl flex-col gap-6 px-6 py-10">
            <div className="text-sm text-muted-foreground">Loading…</div>
          </div>
        </main>
      </AdminShell>
    );
  }

  if (error || !session) {
    return (
      <AdminShell>
        <main className="brand-bg min-h-[calc(100vh-3.5rem)]">
          <div className="mx-auto flex max-w-5xl flex-col gap-6 px-6 py-10">
            <div className="rounded-2xl border border-destructive/30 bg-destructive/5 p-6 text-sm text-destructive">
              {error ?? "Session not found"}
            </div>
          </div>
        </main>
      </AdminShell>
    );
  }

  const summary = session.results.reduce(
    (acc, r) => {
      if (r.result === "PASS") acc.pass += 1;
      else if (r.result === "FAIL") acc.fail += 1;
      else acc.other += 1;
      return acc;
    },
    { pass: 0, fail: 0, other: 0 },
  );

  type CategoryKey = QaCategory | "Removed";
  const byCategory = new Map<CategoryKey, ResultRow[]>();
  for (const r of session.results) {
    const def = findTestCase(r.testCaseId);
    const category: CategoryKey = def ? (def.category as QaCategory) : "Removed";
    const bucket = byCategory.get(category) ?? [];
    bucket.push(r);
    byCategory.set(category, bucket);
  }

  const orderedCategories: CategoryKey[] = [
    ...QA_CATEGORIES.filter((c) => byCategory.has(c)),
    ...(byCategory.has("Removed") ? (["Removed"] as const) : []),
  ];

  const answeredIds = new Set(session.results.map((r) => r.testCaseId));
  const unanswered = QA_TEST_CASES.filter((tc) => !answeredIds.has(tc.id));

  return (
    <AdminShell>
      <main className="brand-bg min-h-[calc(100vh-3.5rem)]">
        <div className="mx-auto flex max-w-5xl flex-col gap-6 px-6 py-10">
          <Link
            to="/admin/qa"
            className="inline-flex items-center gap-1.5 text-[12.5px] font-medium text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="size-3.5" />
            Back to QA runs
          </Link>

          <header className="flex flex-wrap items-start justify-between gap-4">
            <div className="space-y-1">
              <p className="brand-eyebrow">QA run</p>
              <h1 className="brand-heading brand-heading-md">{session.name}</h1>
              <p className="brand-body">
                {session.role} ·{" "}
                <a
                  href={`mailto:${session.email}`}
                  className="underline-offset-4 hover:underline"
                >
                  {session.email}
                </a>
              </p>
            </div>
            <div>
              <SubmittedPill submittedAt={session.submittedAt} />
            </div>
          </header>

          <Card className="grid grid-cols-2 gap-4 p-5 sm:grid-cols-4">
            <Stat label="Total answered" value={session.results.length} />
            <Stat label="Pass" value={summary.pass} tone="emerald" />
            <Stat label="Fail" value={summary.fail} tone="red" />
            <Stat label="Other" value={summary.other} tone="amber" />
          </Card>

          <Card className="space-y-2.5 p-5 text-sm">
            <MetaRow icon={UserIcon} label="Started">
              <LocalDateTime iso={session.startedAt} preset="long" />
            </MetaRow>
            {session.submittedAt ? (
              <MetaRow icon={CheckCircle2} label="Submitted">
                <LocalDateTime iso={session.submittedAt} preset="long" />
              </MetaRow>
            ) : null}
            <MetaRow icon={Mail} label="Email">
              {session.email}
            </MetaRow>
            {session.userAgent ? (
              <MetaRow icon={Monitor} label="User agent">
                <span className="break-all font-mono text-[11px] text-muted-foreground">
                  {session.userAgent}
                </span>
              </MetaRow>
            ) : null}
          </Card>

          {session.notes ? (
            <Card className="space-y-2 p-5">
              <p className="text-[10.5px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                Overall notes
              </p>
              <p className="whitespace-pre-wrap text-sm text-foreground/90">
                {session.notes}
              </p>
            </Card>
          ) : null}

          <section className="space-y-5">
            {orderedCategories.map((category) => {
              const rows = byCategory.get(category) ?? [];
              return (
                <div key={category} className="space-y-2">
                  <h2 className="text-sm font-semibold text-muted-foreground">
                    {category}
                    <span className="ml-2 font-normal text-muted-foreground/80">
                      · {rows.length}
                    </span>
                  </h2>
                  <ul className="space-y-2">
                    {rows.map((r) => (
                      <ResultCard key={r.id} result={r} />
                    ))}
                  </ul>
                </div>
              );
            })}
          </section>

          {unanswered.length > 0 ? (
            <section className="space-y-2">
              <h2 className="text-sm font-semibold text-muted-foreground">
                Unanswered ·{" "}
                <span className="font-normal text-muted-foreground/80">
                  {unanswered.length} test{" "}
                  {unanswered.length === 1 ? "case" : "cases"} weren&apos;t marked
                </span>
              </h2>
              <Card className="overflow-hidden">
                <ul className="divide-y">
                  {unanswered.map((tc) => (
                    <li
                      key={tc.id}
                      className="flex items-start gap-3 px-4 py-3 text-sm"
                    >
                      <span className="mt-0.5 font-mono text-[10.5px] uppercase text-muted-foreground">
                        {tc.id}
                      </span>
                      <span className="text-muted-foreground">{tc.title}</span>
                    </li>
                  ))}
                </ul>
              </Card>
            </section>
          ) : null}
        </div>
      </main>
    </AdminShell>
  );
}

function SubmittedPill({ submittedAt }: { submittedAt: string | null }) {
  if (submittedAt) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-3 py-1 text-[11px] font-medium uppercase tracking-[0.06em] text-emerald-800 ring-1 ring-inset ring-emerald-200/60">
        <CheckCircle2 className="size-3" />
        Submitted
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-3 py-1 text-[11px] font-medium uppercase tracking-[0.06em] text-amber-800 ring-1 ring-inset ring-amber-200/60">
      In progress
    </span>
  );
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone?: "emerald" | "red" | "amber";
}) {
  const tones: Record<string, string> = {
    emerald: "text-emerald-700",
    red: "text-red-700",
    amber: "text-amber-700",
  };
  return (
    <div>
      <p className="text-[10.5px] font-medium uppercase tracking-[0.08em] text-muted-foreground">
        {label}
      </p>
      <p
        className={cn(
          "mt-1 text-2xl font-semibold tabular-nums",
          tone ? tones[tone] : "text-foreground",
        )}
      >
        {value}
      </p>
    </div>
  );
}

function MetaRow({
  icon: Icon,
  label,
  children,
}: {
  icon: typeof UserIcon;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-start gap-3">
      <Icon className="mt-0.5 size-3.5 text-muted-foreground" />
      <div className="min-w-0 flex-1">
        <p className="text-[10.5px] font-medium uppercase tracking-[0.08em] text-muted-foreground">
          {label}
        </p>
        <div className="text-[13px] text-foreground/90">{children}</div>
      </div>
    </div>
  );
}

function ResultCard({ result }: { result: ResultRow }) {
  const tone =
    result.result === "PASS"
      ? "border-emerald-300/55 bg-emerald-500/5"
      : result.result === "FAIL"
        ? "border-red-300/55 bg-red-500/10"
        : "border-amber-300/55 bg-amber-500/10";

  return (
    <li className={cn("rounded-xl border bg-card p-4", tone)}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-mono text-[10.5px] uppercase tracking-[0.08em] text-muted-foreground">
            {result.testCaseId}
          </p>
          <p className="mt-0.5 text-[14px] font-medium">{result.testCaseTitle}</p>
        </div>
        <ResultBadge result={result.result} />
      </div>
      {result.notes ? (
        <p className="mt-3 whitespace-pre-wrap rounded-lg bg-background/70 p-3 text-[13px] leading-relaxed text-foreground/85 ring-1 ring-inset ring-border/70">
          {result.notes}
        </p>
      ) : null}
    </li>
  );
}

function ResultBadge({ result }: { result: "PASS" | "FAIL" | "OTHER" }) {
  if (result === "PASS") {
    return (
      <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-0.5 text-[11.5px] font-medium uppercase tracking-[0.06em] text-emerald-800 ring-1 ring-inset ring-emerald-200/60">
        <CheckCircle2 className="size-3" />
        Pass
      </span>
    );
  }
  if (result === "FAIL") {
    return (
      <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-red-50 px-2.5 py-0.5 text-[11.5px] font-medium uppercase tracking-[0.06em] text-red-800 ring-1 ring-inset ring-red-200/60">
        <CircleAlert className="size-3" />
        Fail
      </span>
    );
  }
  return (
    <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-amber-50 px-2.5 py-0.5 text-[11.5px] font-medium uppercase tracking-[0.06em] text-amber-800 ring-1 ring-inset ring-amber-200/60">
      <CircleHelp className="size-3" />
      Other
    </span>
  );
}
